import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Autocomplete,
  Chip,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays, isAfter, isBefore, startOfDay } from 'date-fns';
import { useSearch } from '@/contexts/SearchContext';
import { useLocationSuggestions } from '@/hooks/api/usePropertySearch';
import { PropertySearchParams } from '@/contexts/SearchContext';

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  onSearch: (params: PropertySearchParams) => void;
  initialParams?: PropertySearchParams;
}

interface GuestCount {
  adults: number;
  children: number;
  infants: number;
}

const SearchModal: React.FC<SearchModalProps> = ({
  open,
  onClose,
  onSearch,
  initialParams = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { state } = useSearch();

  // Form state
  const [location, setLocation] = useState(initialParams.location || '');
  const [checkIn, setCheckIn] = useState<Date | null>(
    initialParams.checkIn ? new Date(initialParams.checkIn) : null
  );
  const [checkOut, setCheckOut] = useState<Date | null>(
    initialParams.checkOut ? new Date(initialParams.checkOut) : null
  );
  const [guestCount, setGuestCount] = useState<GuestCount>({
    adults: Math.max(1, (initialParams.guests || 1) - 0), // Assume all guests are adults for simplicity
    children: 0,
    infants: 0,
  });
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [activeSection, setActiveSection] = useState<'where' | 'checkin' | 'checkout' | 'who' | null>(null);

  // Location autocomplete
  const [locationQuery, setLocationQuery] = useState(location);
  const { data: locationSuggestions = [], isLoading: isLoadingSuggestions } = useLocationSuggestions(
    locationQuery,
    locationQuery.length >= 2
  );

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setLocation(initialParams.location || '');
      setCheckIn(initialParams.checkIn ? new Date(initialParams.checkIn) : null);
      setCheckOut(initialParams.checkOut ? new Date(initialParams.checkOut) : null);
      setGuestCount({
        adults: Math.max(1, (initialParams.guests || 1)),
        children: 0,
        infants: 0,
      });
      setActiveSection('where');
    }
  }, [open, initialParams]);

  // Validation
  const isFormValid = useCallback(() => {
    if (!location.trim()) return false;
    if (!checkIn || !checkOut) return false;
    if (isAfter(checkIn, checkOut)) return false;
    if (guestCount.adults < 1) return false;
    return true;
  }, [location, checkIn, checkOut, guestCount.adults]);

  // Handle date changes with validation
  const handleCheckInChange = (date: Date | null) => {
    setCheckIn(date);
    if (date && checkOut && isAfter(date, checkOut)) {
      setCheckOut(addDays(date, 1));
    }
    if (date && !checkOut) {
      setActiveSection('checkout');
    }
  };

  const handleCheckOutChange = (date: Date | null) => {
    setCheckOut(date);
    if (date && checkIn && isBefore(date, checkIn)) {
      setCheckIn(addDays(date, -1));
    }
    if (date) {
      setActiveSection('who');
    }
  };

  // Handle guest count changes
  const updateGuestCount = (type: keyof GuestCount, increment: boolean) => {
    setGuestCount(prev => {
      const newCount = { ...prev };
      if (increment) {
        newCount[type] = Math.min(newCount[type] + 1, type === 'adults' ? 16 : 5);
      } else {
        newCount[type] = Math.max(newCount[type] - 1, type === 'adults' ? 1 : 0);
      }
      return newCount;
    });
  };

  // Handle search submission
  const handleSearch = () => {
    if (!isFormValid()) return;

    const totalGuests = guestCount.adults + guestCount.children;
    const searchParams: PropertySearchParams = {
      location: location.trim(),
      checkIn: checkIn ? format(checkIn, 'yyyy-MM-dd') : undefined,
      checkOut: checkOut ? format(checkOut, 'yyyy-MM-dd') : undefined,
      guests: totalGuests,
    };

    onSearch(searchParams);
    onClose();
  };

  // Handle recent search selection
  const handleRecentSearchSelect = (params: PropertySearchParams) => {
    setLocation(params.location || '');
    setCheckIn(params.checkIn ? new Date(params.checkIn) : null);
    setCheckOut(params.checkOut ? new Date(params.checkOut) : null);
    setGuestCount({
      adults: Math.max(1, (params.guests || 1)),
      children: 0,
      infants: 0,
    });
  };

  const totalGuests = guestCount.adults + guestCount.children + guestCount.infants;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            maxHeight: isMobile ? '100vh' : '80vh',
            margin: isMobile ? 0 : 2,
          },
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: isMobile ? 'flex-start' : 'center',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: isMobile ? 2 : 3,
              borderBottom: 1,
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
            }}
          >
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
              Search properties
            </Typography>
            <IconButton 
              onClick={onClose} 
              size={isMobile ? 'medium' : 'large'}
              sx={{
                minWidth: 44,
                minHeight: 44,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Form */}
          <Box sx={{ p: isMobile ? 2 : 3, pb: isMobile ? 4 : 3 }}>
            {/* Main Search Inputs */}
            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: isMobile ? 3 : 4,
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <Grid container>
                {/* Where */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: isMobile ? 3 : 2,
                      borderRight: { md: 1 },
                      borderBottom: { xs: 1, md: 0 },
                      borderColor: 'divider',
                      cursor: 'pointer',
                      backgroundColor: activeSection === 'where' ? 'action.hover' : 'transparent',
                      '&:hover': { backgroundColor: 'action.hover' },
                      minHeight: isMobile ? 72 : 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                    onClick={() => setActiveSection('where')}
                  >
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      WHERE
                    </Typography>
                    <Autocomplete
                      value={location}
                      inputValue={locationQuery}
                      onInputChange={(_, newValue) => {
                        setLocationQuery(newValue);
                        setLocation(newValue);
                      }}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          setLocation(newValue);
                          setActiveSection('checkin');
                        }
                      }}
                      options={locationSuggestions}
                      loading={isLoadingSuggestions}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search destinations"
                          variant="standard"
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: isMobile ? '1.1rem' : '1rem',
                              fontWeight: 500,
                              padding: isMobile ? '12px 0' : '8px 0',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* Check-in */}
                <Grid item xs={6} md={3}>
                  <Box
                    sx={{
                      p: isMobile ? 3 : 2,
                      borderRight: 1,
                      borderColor: 'divider',
                      cursor: 'pointer',
                      backgroundColor: activeSection === 'checkin' ? 'action.hover' : 'transparent',
                      '&:hover': { backgroundColor: 'action.hover' },
                      minHeight: isMobile ? 72 : 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                    onClick={() => setActiveSection('checkin')}
                  >
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      CHECK-IN
                    </Typography>
                    <DatePicker
                      value={checkIn}
                      onChange={handleCheckInChange}
                      minDate={startOfDay(new Date())}
                      maxDate={checkOut ? addDays(checkOut, -1) : undefined}
                      slotProps={{
                        textField: {
                          placeholder: "Add dates",
                          variant: "standard",
                          InputProps: {
                            disableUnderline: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarIcon color="action" />
                              </InputAdornment>
                            ),
                          },
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: '1rem',
                              fontWeight: 500,
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>

                {/* Check-out */}
                <Grid item xs={6} md={3}>
                  <Box
                    sx={{
                      p: isMobile ? 3 : 2,
                      cursor: 'pointer',
                      backgroundColor: activeSection === 'checkout' ? 'action.hover' : 'transparent',
                      '&:hover': { backgroundColor: 'action.hover' },
                      minHeight: isMobile ? 72 : 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                    onClick={() => setActiveSection('checkout')}
                  >
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      CHECK-OUT
                    </Typography>
                    <DatePicker
                      value={checkOut}
                      onChange={handleCheckOutChange}
                      minDate={checkIn ? addDays(checkIn, 1) : addDays(new Date(), 1)}
                      slotProps={{
                        textField: {
                          placeholder: "Add dates",
                          variant: "standard",
                          InputProps: {
                            disableUnderline: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarIcon color="action" />
                              </InputAdornment>
                            ),
                          },
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: '1rem',
                              fontWeight: 500,
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Who - Full width on mobile */}
              <Box
                sx={{
                  p: isMobile ? 3 : 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  backgroundColor: activeSection === 'who' ? 'action.hover' : 'transparent',
                  '&:hover': { backgroundColor: 'action.hover' },
                  minHeight: isMobile ? 72 : 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  setActiveSection('who');
                  setShowGuestPicker(!showGuestPicker);
                }}
              >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  WHO
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <PeopleIcon color="action" sx={{ mr: 1 }} />
                  <Typography 
                    variant="body1" 
                    fontWeight={500}
                    sx={{ 
                      fontSize: isMobile ? '1.1rem' : '1rem',
                    }}
                  >
                    {totalGuests === 1 ? '1 guest' : `${totalGuests} guests`}
                  </Typography>
                </Box>
              </Box>

              {/* Guest Picker */}
              <Fade in={showGuestPicker}>
                <Box
                  sx={{
                    p: 3,
                    borderTop: 1,
                    borderColor: 'divider',
                    backgroundColor: 'grey.50',
                  }}
                >
                  {/* Adults */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        Adults
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ages 13 or above
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton
                        onClick={() => updateGuestCount('adults', false)}
                        disabled={guestCount.adults <= 1}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          minWidth: isMobile ? 44 : 32,
                          minHeight: isMobile ? 44 : 32,
                          '&:disabled': { opacity: 0.3 },
                        }}
                      >
                        -
                      </IconButton>
                      <Typography variant="body1" sx={{ minWidth: 20, textAlign: 'center' }}>
                        {guestCount.adults}
                      </Typography>
                      <IconButton
                        onClick={() => updateGuestCount('adults', true)}
                        disabled={guestCount.adults >= 16}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          minWidth: isMobile ? 44 : 32,
                          minHeight: isMobile ? 44 : 32,
                          '&:disabled': { opacity: 0.3 },
                        }}
                      >
                        +
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Children */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        Children
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ages 2-12
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton
                        onClick={() => updateGuestCount('children', false)}
                        disabled={guestCount.children <= 0}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          minWidth: isMobile ? 44 : 32,
                          minHeight: isMobile ? 44 : 32,
                          '&:disabled': { opacity: 0.3 },
                        }}
                      >
                        -
                      </IconButton>
                      <Typography variant="body1" sx={{ minWidth: 20, textAlign: 'center' }}>
                        {guestCount.children}
                      </Typography>
                      <IconButton
                        onClick={() => updateGuestCount('children', true)}
                        disabled={guestCount.children >= 5}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          minWidth: isMobile ? 44 : 32,
                          minHeight: isMobile ? 44 : 32,
                          '&:disabled': { opacity: 0.3 },
                        }}
                      >
                        +
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Infants */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        Infants
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Under 2
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton
                        onClick={() => updateGuestCount('infants', false)}
                        disabled={guestCount.infants <= 0}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          minWidth: isMobile ? 44 : 32,
                          minHeight: isMobile ? 44 : 32,
                          '&:disabled': { opacity: 0.3 },
                        }}
                      >
                        -
                      </IconButton>
                      <Typography variant="body1" sx={{ minWidth: 20, textAlign: 'center' }}>
                        {guestCount.infants}
                      </Typography>
                      <IconButton
                        onClick={() => updateGuestCount('infants', true)}
                        disabled={guestCount.infants >= 5}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          minWidth: isMobile ? 44 : 32,
                          minHeight: isMobile ? 44 : 32,
                          '&:disabled': { opacity: 0.3 },
                        }}
                      >
                        +
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Paper>

            {/* Recent Searches */}
            {state.recentSearches.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Recent searches
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {state.recentSearches.slice(0, 3).map((search, index) => (
                    <Chip
                      key={index}
                      label={`${search.location || 'Anywhere'} • ${search.guests || 1} guest${(search.guests || 1) > 1 ? 's' : ''}`}
                      variant="outlined"
                      onClick={() => handleRecentSearchSelect(search)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Search Button */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: isMobile ? 'center' : 'flex-end',
                position: isMobile ? 'sticky' : 'static',
                bottom: isMobile ? 0 : 'auto',
                backgroundColor: isMobile ? 'background.paper' : 'transparent',
                p: isMobile ? 2 : 0,
                mx: isMobile ? -2 : 0,
                borderTop: isMobile ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleSearch}
                disabled={!isFormValid()}
                startIcon={<SearchIcon />}
                fullWidth={isMobile}
                sx={{
                  borderRadius: 2,
                  px: isMobile ? 3 : 4,
                  py: isMobile ? 2 : 1.5,
                  fontSize: isMobile ? '1.1rem' : '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: isMobile ? 56 : 'auto',
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&:disabled': {
                    backgroundColor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SearchModal;